/**
 * Service pour détecter et gérer l'état du backend API
 */

let backendHealthStatus: 'unknown' | 'healthy' | 'unhealthy' = 'unknown';
let lastHealthCheck = 0;
const HEALTH_CHECK_INTERVAL = 30000; // 30 secondes

// ✨ Listeners pour notifier les composants du changement d'état
type HealthListener = (isHealthy: boolean) => void;
const healthListeners: Set<HealthListener> = new Set();

/**
 * S'abonner aux changements d'état de santé du backend
 */
export function subscribeToHealthChanges(listener: HealthListener): () => void {
  healthListeners.add(listener);
  // Notifier immédiatement avec l'état actuel si connu
  if (backendHealthStatus !== 'unknown') {
    listener(backendHealthStatus === 'healthy');
  }
  return () => healthListeners.delete(listener);
}

/**
 * Notifier tous les listeners du changement d'état
 */
function notifyHealthChange(isHealthy: boolean): void {
  healthListeners.forEach(listener => listener(isHealthy));
}

/**
 * Vérifie si le backend est disponible
 */
export async function checkBackendHealth(): Promise<boolean> {
  const now = Date.now();
  
  // Éviter les vérifications trop fréquentes
  if (now - lastHealthCheck < HEALTH_CHECK_INTERVAL && backendHealthStatus !== 'unknown') {
    return backendHealthStatus === 'healthy';
  }
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5s timeout
    
    // Essayer un endpoint simple ou de santé
    const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/land/api/v1'}/health`, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Accept': 'application/json'
      }
    });
    
    clearTimeout(timeoutId);
    
    const previousStatus = backendHealthStatus;
    
    if (response.ok) {
      backendHealthStatus = 'healthy';
      lastHealthCheck = now;
      console.log('✅ Backend API est disponible');
      
      // Notifier si le statut a changé
      if (previousStatus !== 'healthy') {
        notifyHealthChange(true);
      }
      return true;
    } else {
      console.warn(`⚠️ Backend API répond avec status ${response.status}`);
      backendHealthStatus = 'unhealthy';
      lastHealthCheck = now;
      
      if (previousStatus === 'healthy') {
        notifyHealthChange(false);
      }
      return false;
    }
  } catch (error) {
    console.warn('⚠️ Backend API non disponible:', error);
    const previousStatus = backendHealthStatus;
    backendHealthStatus = 'unhealthy';
    lastHealthCheck = now;
    
    if (previousStatus === 'healthy') {
      notifyHealthChange(false);
    }
    return false;
  }
}

/**
 * Récupère le statut actuel du backend sans faire de requête
 */
export function getBackendHealthStatus(): 'unknown' | 'healthy' | 'unhealthy' {
  return backendHealthStatus;
}

/**
 * Force une nouvelle vérification du backend
 */
export function forceBackendHealthCheck(): Promise<boolean> {
  lastHealthCheck = 0;
  return checkBackendHealth();
}

/**
 * ✨ NOUVEAU: Attendre que le backend soit disponible
 * Utile pour les composants qui doivent charger des données au démarrage
 * @param maxAttempts Nombre maximum de tentatives (défaut: 3)
 * @param delayMs Délai entre chaque tentative (défaut: 2000ms)
 */
export async function waitForBackendHealth(maxAttempts = 3, delayMs = 2000): Promise<boolean> {
  // Si déjà healthy, retourner immédiatement
  if (backendHealthStatus === 'healthy') {
    return true;
  }
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    console.log(`🔄 Vérification backend (tentative ${attempt}/${maxAttempts})...`);
    
    const isHealthy = await forceBackendHealthCheck();
    if (isHealthy) {
      console.log('✅ Backend prêt!');
      return true;
    }
    
    if (attempt < maxAttempts) {
      console.log(`⏳ Attente ${delayMs/1000}s avant nouvelle tentative...`);
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }
  
  console.warn('❌ Backend non disponible après toutes les tentatives');
  return false;
}

/**
 * ✨ NOUVEAU: Vérifier si le backend est prêt (non-bloquant)
 * Retourne true si healthy, false sinon, sans bloquer
 */
export function isBackendReady(): boolean {
  return backendHealthStatus === 'healthy';
}
